import ExpandMore from "@mui/icons-material/ExpandMore";
import Label from "@mui/icons-material/Label";
import Person from "@mui/icons-material/Person";
import PersonAddAlt1 from "@mui/icons-material/PersonAddAlt1";
import PersonRemove from "@mui/icons-material/PersonRemove";
import Timer from "@mui/icons-material/Timer";
import { Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { UserSelectDialog } from "components/UserSelectDialog";
import { useUpdateActivityAssignees } from "hooks/activities";
import { useGridSelection } from "hooks/omnisearch-v2";
import { useCurrentUser } from "hooks/useCurrentUser";
import { compact } from "lodash-es";
import { useCallback, useState } from "react";

import { assignableRows, ORIGIN_OWNS_ASSIGNMENT, UnifiedWorkRow } from "./types";
import { useRefetchWorkQueue } from "./useRefetchWorkQueue";
import { WorkEstimateDialog } from "./WorkEstimateDialog";
import { WorkTagsDialog } from "./WorkTagsDialog";

/**
 * How the Assign… dialog names its target. The plain title stands unless the
 * selection carries rows whose submission owns their assignee, in which case the
 * count says so before anyone picks a person — the mutation's own success alert
 * then reports how many rows were actually written.
 */
function assignDialogTitle(rows: UnifiedWorkRow[] | null): string {
  const plain = "Assign work to";
  if (!rows) {
    return plain;
  }
  const originOwned = rows.filter((row) => row.assigneeReadOnly).length;
  if (originOwned === 0) {
    return plain;
  }
  return `${plain} — skipping ${originOwned} of ${rows.length} assigned on their submission`;
}

type BulkActionsMenuProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  assigning: boolean;
  canAssignToMe: boolean;
  onAssign: () => void;
  onAssignToMe: () => void;
  onUnassign: () => void;
  onManageTags: () => void;
  onSetEstimate: () => void;
};

/** The dropdown's items. Each closes the menu before acting on the selection. */
function BulkActionsMenu({
  anchorEl,
  open,
  onClose,
  assigning,
  canAssignToMe,
  onAssign,
  onAssignToMe,
  onUnassign,
  onManageTags,
  onSetEstimate,
}: BulkActionsMenuProps) {
  return (
    <Menu
      id="work-bulk-actions-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      // Prevent menu from affecting body scroll, which triggers DataGrid's ResizeObserver
      disableScrollLock
      MenuListProps={{ "aria-labelledby": "work-bulk-actions-button" }}
    >
      <MenuItem disabled={assigning} onClick={onAssign}>
        <ListItemIcon>
          <PersonAddAlt1 fontSize="small" />
        </ListItemIcon>
        <ListItemText>Assign…</ListItemText>
      </MenuItem>
      <MenuItem disabled={assigning || !canAssignToMe} onClick={onAssignToMe}>
        <ListItemIcon>
          <Person fontSize="small" />
        </ListItemIcon>
        <ListItemText>Assign to me</ListItemText>
      </MenuItem>
      <MenuItem disabled={assigning} onClick={onUnassign}>
        <ListItemIcon>
          <PersonRemove fontSize="small" />
        </ListItemIcon>
        <ListItemText>Unassign</ListItemText>
      </MenuItem>
      <Divider />
      <MenuItem onClick={onManageTags}>
        <ListItemIcon>
          <Label fontSize="small" />
        </ListItemIcon>
        <ListItemText>Manage Tags…</ListItemText>
      </MenuItem>
      <MenuItem onClick={onSetEstimate}>
        <ListItemIcon>
          <Timer fontSize="small" />
        </ListItemIcon>
        <ListItemText>Set Estimate…</ListItemText>
      </MenuItem>
    </Menu>
  );
}

/**
 * The queue's Bulk Actions dropdown — the pattern the other staff grids use.
 * Rendered as a grid command button; disabled until rows are selected.
 * Assignment reuses the fulfillment grid's updateActivityAssignees mutation
 * against each row's current activity; tags and estimates apply per workflow.
 */
export function WorkBulkActions() {
  const { user } = useCurrentUser();
  const { selectedCount, getSelectedRows, clearSelection } = useGridSelection<UnifiedWorkRow>();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  // Rows captured when the dialog opened, so the mutation applies to that
  // snapshot rather than a selection the user has since changed.
  const [assignTarget, setAssignTarget] = useState<UnifiedWorkRow[] | null>(null);
  const [tagsTarget, setTagsTarget] = useState<UnifiedWorkRow[] | null>(null);
  const [estimateTarget, setEstimateTarget] = useState<UnifiedWorkRow[] | null>(null);

  const menuOpen = Boolean(menuAnchor);
  const closeMenu = () => setMenuAnchor(null);

  const alerts = useAlerts();
  const refetchQueue = useRefetchWorkQueue();
  const [updateAssignees, { loading: assigning }] = useUpdateActivityAssignees(refetchQueue);

  const applyAssignee = useCallback(
    async (rows: UnifiedWorkRow[], assigneeId: string | null) => {
      const ids = compact(assignableRows(rows).map((row) => row.activityId));
      // Every selected row is origin-owned or has no step to hang an assignee
      // off. Returning quietly here is indistinguishable from a click that
      // failed, so say which it is — the reason is the same one the drawer
      // gives for a single row.
      if (ids.length === 0) {
        alerts.info(ORIGIN_OWNS_ASSIGNMENT);
        return;
      }
      await updateAssignees({ variables: { ids, assigneeId } });
      clearSelection();
    },
    [updateAssignees, clearSelection, alerts]
  );

  const openAssign = useCallback(
    (rows: UnifiedWorkRow[]) => {
      // Same silence, one step earlier: with nothing assignable the dialog
      // would open onto a mutation that could write nothing, so it does not
      // open and the reason is said out loud instead.
      if (assignableRows(rows).length === 0) {
        alerts.info(ORIGIN_OWNS_ASSIGNMENT);
        return;
      }
      setAssignTarget(rows);
    },
    [alerts]
  );

  return (
    <>
      <Button
        id="work-bulk-actions-button"
        aria-controls={menuOpen ? "work-bulk-actions-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={menuOpen ? "true" : undefined}
        onClick={(event) => setMenuAnchor(event.currentTarget)}
        endIcon={<ExpandMore />}
        variant="contained"
        color="staff"
        disabled={selectedCount === 0}
        sx={{ textTransform: "uppercase" }}
      >
        Bulk Actions
      </Button>
      <BulkActionsMenu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={closeMenu}
        assigning={assigning}
        canAssignToMe={Boolean(user)}
        onAssign={() => {
          closeMenu();
          openAssign(getSelectedRows());
        }}
        onAssignToMe={() => {
          closeMenu();
          if (user) {
            void applyAssignee(getSelectedRows(), user.id);
          }
        }}
        onUnassign={() => {
          closeMenu();
          void applyAssignee(getSelectedRows(), null);
        }}
        onManageTags={() => {
          closeMenu();
          setTagsTarget(getSelectedRows());
        }}
        onSetEstimate={() => {
          closeMenu();
          setEstimateTarget(getSelectedRows());
        }}
      />
      <BulkActionDialogs
        assignTarget={assignTarget}
        setAssignTarget={setAssignTarget}
        tagsTarget={tagsTarget}
        setTagsTarget={setTagsTarget}
        estimateTarget={estimateTarget}
        setEstimateTarget={setEstimateTarget}
        applyAssignee={applyAssignee}
        onSaved={() => {
          refetchQueue();
          clearSelection();
        }}
      />
    </>
  );
}

type BulkActionDialogsProps = {
  assignTarget: UnifiedWorkRow[] | null;
  setAssignTarget: (rows: UnifiedWorkRow[] | null) => void;
  tagsTarget: UnifiedWorkRow[] | null;
  setTagsTarget: (rows: UnifiedWorkRow[] | null) => void;
  estimateTarget: UnifiedWorkRow[] | null;
  setEstimateTarget: (rows: UnifiedWorkRow[] | null) => void;
  applyAssignee: (rows: UnifiedWorkRow[], assigneeId: string | null) => Promise<void>;
  onSaved: () => void;
};

/**
 * The overlays the bulk menu can raise. Each is driven by its captured row
 * snapshot rather than the live selection, so a mid-dialog selection change
 * cannot retarget the mutation.
 */
function BulkActionDialogs({
  assignTarget,
  setAssignTarget,
  tagsTarget,
  setTagsTarget,
  estimateTarget,
  setEstimateTarget,
  applyAssignee,
  onSaved,
}: BulkActionDialogsProps) {
  return (
    <>
      <WorkTagsDialog
        rows={tagsTarget ?? []}
        open={tagsTarget !== null}
        onClose={() => setTagsTarget(null)}
        onSaved={onSaved}
      />
      <WorkEstimateDialog
        rows={estimateTarget ?? []}
        open={estimateTarget !== null}
        onClose={() => setEstimateTarget(null)}
        onSaved={onSaved}
      />
      <UserSelectDialog
        open={assignTarget !== null}
        title={assignDialogTitle(assignTarget)}
        onClose={() => setAssignTarget(null)}
        onSelect={async (userId) => {
          if (assignTarget) {
            await applyAssignee(assignTarget, userId);
          }
          setAssignTarget(null);
        }}
      />
    </>
  );
}
